import React, { useEffect, useMemo, useState } from 'react';
import './OnboardingModal.css';

const DEFAULT_SLIDES = [
  {
    id: 'welcome',
    title: '👋 Welcome to Pulc — your AI marker & study coach',
    description: 'Meet your personal study assistant packed with tools to grade essays, solve maths, and turn notes into quizzes.',
    nextLabel: "Let's go →",
  },
  {
    id: 'essay',
    title: 'Snap your essay – get a grade & instant feedback',
    videoSrc: 'https://pulcvideos.s3.eu-north-1.amazonaws.com/OnBoarding1Essay.mp4',
    nextLabel: 'Next',
  },
  {
    id: 'maths',
    title: 'Stuck on maths? Get step-by-step solutions.',
    videoSrc: 'https://pulcvideos.s3.eu-north-1.amazonaws.com/OnBoarding2Maths.mp4',
    nextLabel: 'Next',
  },
  {
    id: 'quiz',
    title: 'Turn notes into quizzes — study smarter & faster.',
    videoSrc: 'https://pulcvideos.s3.eu-north-1.amazonaws.com/OnBoarding3Quiz.mp4',
    nextLabel: 'Next',
  },
  {
    id: 'actions',
    title: 'What do you want to do first?',
    description: 'Pick a starting point — you can explore everything from the sidebar anytime.',
    actions: [
      { label: 'Mark an Essay', value: 'mark-essay' },
      { label: 'Solve Maths Problem', value: 'solve-maths' },
      { label: 'Create Quiz', value: 'create-quiz' },
      { label: 'Explore', value: 'explore' },
    ],
    nextLabel: 'Finish',
  },
];

const getVideoType = (src = '') => {
  const extension = src.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'mp4':
      return 'video/mp4';
    case 'mov':
      return 'video/quicktime';
    case 'webm':
      return 'video/webm';
    default:
      return undefined;
  }
};

const OnboardingModal = ({ isOpen, onClose, onActionSelect, slides }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slideData = useMemo(() => (
    slides && slides.length > 0 ? slides : DEFAULT_SLIDES
  ), [slides]);

  useEffect(() => {
    if (isOpen) {
      setCurrentSlide(0);
    }
  }, [isOpen, slideData]);

  const slide = useMemo(
    () => slideData[currentSlide] || slideData[0],
    [currentSlide, slideData]
  );
  const isLastSlide = currentSlide === slideData.length - 1;

  if (!isOpen) {
    return null;
  }

  const handleBack = () => {
    if (currentSlide === 0) {
      return;
    }
    setCurrentSlide((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    if (isLastSlide) {
      onClose?.();
      return;
    }
    setCurrentSlide((prev) => Math.min(prev + 1, slideData.length - 1));
  };

  const handleActionClick = (value) => {
    if (onActionSelect) {
      onActionSelect(value);
    }
    onClose?.();
  };

  return (
    <div className="onboarding-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
      <div className="onboarding-modal">
        <button
          className="onboarding-close"
          onClick={() => onClose?.()}
          aria-label="Close onboarding"
        >
          ×
        </button>

        <div className="onboarding-content">
          <h2 id="onboarding-title" className="onboarding-title">{slide.title}</h2>
          {slide.description && <p className="onboarding-description">{slide.description}</p>}

          {slide.videoSrc && (
            <div className="onboarding-video-wrapper">
              <video
                key={slide.videoSrc}
                controls
                autoPlay
                loop
                muted
                playsInline
              >
                <source src={slide.videoSrc} type={getVideoType(slide.videoSrc)} />
                Your browser does not support the video tag.
              </video>
            </div>
          )}

          {slide.actions && (
            <div className="onboarding-actions">
              {slide.actions.map((action) => (
                <button
                  key={action.value}
                  className="onboarding-action-button"
                  onClick={() => handleActionClick(action.value)}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="onboarding-footer">
          <div className="onboarding-progress">
            {slideData.map((item, index) => (
              <span
                key={item.id}
                className={`onboarding-progress-dot${index === currentSlide ? ' active' : ''}`}
              />
            ))}
          </div>

          <div className="onboarding-navigation">
            <button
              type="button"
              className="onboarding-nav-button"
              onClick={handleBack}
              disabled={currentSlide === 0}
            >
              Back
            </button>
            <button
              type="button"
              className="onboarding-nav-button onboarding-nav-button-primary"
              onClick={handleNext}
            >
              {slide.nextLabel || (isLastSlide ? 'Finish' : 'Next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;

